// Polymarket US REST client; values normalised into the shared MC/MQ/Cash units.

import { type ClientOptions, RestTransport } from '../venue/rest';
import { type MC, type MQ, MQ_PER_CONTRACT, mcToDollarString } from '../core/money';
import { AmbiguousPlacement, VenueAPIError } from '../venue/rest';
import {
  type PmBalance, type PmBbo, type PmBook, type PmEvent, type PmFill, type PmMarket, type PmOrder, type PmPosition, type PmResolution,
  parseBalance, parseBbo, parseBook, parseEvent, parseMarket, parseOrder, parsePlaceResponse, parsePosition,
  parseResolutionActivity, parseTradeActivity,
} from './parse';

export interface PmPlacePayload {
  slug: string;
  /** The contract being BOUGHT, as the panel thinks of it. */
  outcome: 'yes' | 'no';
  /** Price of that contract, in MC (the panel's units). */
  priceMc: MC;
  qtyMq: MQ;
  /** post: rest only (participateDontInitiate, GTC). fok: fill or kill. */
  mode: 'post' | 'fok';
  /** Journal tag (arb order and leg). */
  tag?: string;
}

export type { PmBalance, PmBbo, PmBook, PmEvent, PmExecution, PmFill, PmLevel, PmMarket, PmOrder, PmPosition, PmResolution } from './parse';
export { parseBalance, parseBbo, parseBook, parseEvent, parseExecution, parseMarket, parseOrder, parsePlaceResponse, parsePosition, parsePositionDelta } from './parse';

export class PolymarketClient extends RestTransport {
  constructor(opts: ClientOptions = {}) {
    // 6 req/s avoids 429s the gateway edge throws well below the documented 20/s budget.
    super({ venue: 'polymarket', source: 'pm-rest', read: [6, 6], ...opts });
  }

  async getBalances(): Promise<PmBalance[]> {
    const d = await this.getJson('/v1/account/balances');
    return ((d.balances ?? []) as any[]).map(parseBalance);
  }

  async getPositions(): Promise<PmPosition[]> {
    const out: PmPosition[] = [];
    let cursor = '';
    for (let page = 0; page < 20; page++) {
      const d = await this.getJson('/v1/portfolio/positions', { params: { limit: 100, cursor: cursor || undefined } });
      const map = (d.positions ?? {}) as Record<string, any>;
      for (const [slug, p] of Object.entries(map)) out.push(parsePosition(slug, p));
      cursor = String(d.nextCursor ?? '');
      if (d.eof || !cursor) break;
    }
    return out;
  }

  async getOpenOrders(): Promise<PmOrder[]> {
    const d = await this.getJson('/v1/orders/open');
    return ((d.orders ?? []) as any[]).map(parseOrder);
  }

  /** All trades and resolutions, oldest first; the only source of fill history and realised P&L. */
  async getActivities(opts: { marketSlug?: string; maxPages?: number } = {}): Promise<{ fills: PmFill[]; resolutions: PmResolution[] }> {
    const fills: PmFill[] = [];
    const resolutions: PmResolution[] = [];
    let cursor = '';
    for (let page = 0; page < (opts.maxPages ?? 40); page++) {
      const d = await this.getJson('/v1/portfolio/activities', {
        params: {
          limit: 100,
          cursor: cursor || undefined,
          marketSlug: opts.marketSlug,
          types: ['ACTIVITY_TYPE_TRADE', 'ACTIVITY_TYPE_POSITION_RESOLUTION'],
        },
        timeoutMs: 15_000,
      });
      for (const a of (d.activities ?? []) as any[]) {
        if (a?.type === 'ACTIVITY_TYPE_TRADE') {
          const f = parseTradeActivity(a);
          if (f) fills.push(f);
        } else if (a?.type === 'ACTIVITY_TYPE_POSITION_RESOLUTION') {
          const r = parseResolutionActivity(a);
          if (r) resolutions.push(r);
        }
      }
      cursor = String(d.nextCursor ?? '');
      if (d.eof || !cursor) break;
    }
    fills.sort((a, b) => a.t - b.t);
    resolutions.sort((a, b) => a.t - b.t);
    return { fills, resolutions };
  }

  async getOrder(id: string): Promise<PmOrder> {
    const d = await this.getJson(`/v1/order/${encodeURIComponent(id)}`);
    return parseOrder(d.order ?? d);
  }

  /** Every order quotes the YES price; a NO buy at X sends price.value = 1 − X with outcomeSide NO. */
  async placeOrder(p: PmPlacePayload): Promise<PmOrder> {
    const yesPriceMc = p.outcome === 'yes' ? p.priceMc : 1000 - p.priceMc;
    const body = {
      marketSlug: p.slug,
      type: 'ORDER_TYPE_LIMIT',
      price: { value: mcToDollarString(yesPriceMc), currency: 'USD' },
      quantity: p.qtyMq / MQ_PER_CONTRACT,
      tif: p.mode === 'fok' ? 'TIME_IN_FORCE_FILL_OR_KILL' : 'TIME_IN_FORCE_GOOD_TILL_CANCEL',
      outcomeSide: p.outcome === 'yes' ? 'OUTCOME_SIDE_YES' : 'OUTCOME_SIDE_NO',
      action: 'ORDER_ACTION_BUY',
      ...(p.mode === 'post' ? { participateDontInitiate: true } : {}),
      // FOK only: without this a killed order looks resting until the next poll.
      ...(p.mode === 'fok' ? { synchronousExecution: true, maxBlockTime: 3 } : {}),
    };
    const r = await this.request('POST', '/v1/orders', { json: body, timeoutMs: 15_000, tag: p.tag });
    if (r.status >= 500) throw new AmbiguousPlacement(`HTTP ${r.status}`);
    if (r.status >= 300) throw new VenueAPIError(r.status, r.text);
    return parsePlaceResponse(JSON.parse(r.text || '{}'));
  }

  /** Cancel one order; the venue wants the market slug alongside the id. */
  async cancelOrder(id: string, slug: string, tag?: string): Promise<number> {
    const r = await this.request('POST', `/v1/order/${encodeURIComponent(id)}/cancel`, { json: { marketSlug: slug }, timeoutMs: 10_000, tag });
    if (r.status >= 300) throw new VenueAPIError(r.status, r.text);
    return r.status;
  }

  /** Public. One page of active, unclosed markets (500 cap); the index pages through. */
  async listMarkets(opts: {
    limit?: number;
    offset?: number;
    categories?: string[];
    sportsMarketTypes?: string[];
    endDateMax?: string;
  } = {}): Promise<PmMarket[]> {
    const d = await this.getJson('/v1/markets', {
      base: 'public',
      params: {
        active: 'true',
        closed: 'false',
        limit: opts.limit ?? 500,
        offset: opts.offset || undefined,
        categories: opts.categories,
        sportsMarketTypes: opts.sportsMarketTypes,
        endDateMax: opts.endDateMax,
      },
      timeoutMs: 15_000,
    });
    return ((d.markets ?? []) as any[]).map(parseMarket).filter((m) => m.slug);
  }

  /** Public, undocumented text search over events; param is `query=`, not `q=`. */
  async searchEvents(query: string, limit = 8): Promise<PmEvent[]> {
    const d = await this.getJson('/v1/search', { base: 'public', params: { query, limit }, timeoutMs: 20_000 });
    return ((d.events ?? []) as any[]).map(parseEvent).filter((e) => e.slug);
  }

  /** Public. One event with all its markets; the slug is the one in the site URL. */
  async getEvent(slug: string): Promise<PmEvent> {
    const d = await this.getJson(`/v1/events/slug/${encodeURIComponent(slug)}`, { base: 'public', timeoutMs: 20_000 });
    return parseEvent(d.event ?? d);
  }

  /** Public. */
  async getMarket(slug: string): Promise<PmMarket> {
    const d = await this.getJson(`/v1/market/slug/${encodeURIComponent(slug)}`, { base: 'public' });
    return parseMarket(d.market ?? d);
  }

  /** Public. Top of book with shares traded and open interest — the only volume the venue exposes. */
  async getBbo(slug: string): Promise<PmBbo> {
    const d = await this.getJson(`/v1/markets/${encodeURIComponent(slug)}/bbo`, { base: 'public', timeoutMs: 6_000 });
    const b = parseBbo(d.marketData ?? d);
    if (!b.slug) b.slug = slug;
    return b;
  }

  /** Public. The REST snapshot; the socket carries the same shape live. */
  async getBook(slug: string): Promise<PmBook> {
    const d = await this.getJson(`/v1/markets/${encodeURIComponent(slug)}/book`, { base: 'public', timeoutMs: 6_000 });
    const b = parseBook(d.marketData ?? d);
    if (!b.slug) b.slug = slug;
    return b;
  }
}
